export const LMS_FILTER_ALL = 'ALL';

const LMS_VALUES = new Set(['d2l', 'canvas']);

/**
 * Normalize API / UI LMS tokens to canonical values used by filters and actions.
 * @param {*} value
 * @returns {'d2l'|'canvas'}
 */
export const normalizeDeliveryLms = (value) => {
  if (value == null || value === '') return 'd2l';
  const token = String(value).trim().toLowerCase();
  return LMS_VALUES.has(token) ? token : 'd2l';
};

/**
 * Client-side facility list filter (search + LMS).
 * @param {Array} rows
 * @param {{ search?: string, lmsFilter?: string }} options
 * @returns {Array}
 */
export const filterFacilityRows = (rows, { search = '', lmsFilter = LMS_FILTER_ALL } = {}) => {
  const term = search.trim().toLowerCase();
  return rows.filter((row) => {
    const matchesSearch =
      !term ||
      (row.locationName || '').toLowerCase().includes(term) ||
      (row.shortCode || '').toLowerCase().includes(term);
    const lms = normalizeDeliveryLms(row.currentDeliveryLms);
    const matchesLms = lmsFilter === LMS_FILTER_ALL || lms === lmsFilter;
    return matchesSearch && matchesLms;
  });
};

/**
 * Fleet-level migration summary for the cutover stat strip.
 * @param {Array} rows
 * @returns {{ total: number, canvas: number, d2l: number, percentMigrated: number }}
 */
export const summarizeFacilities = (rows = []) => {
  const total = rows.length;
  const canvas = rows.filter((row) => normalizeDeliveryLms(row.currentDeliveryLms) === 'canvas').length;
  const d2l = total - canvas;
  const percentMigrated = total === 0 ? 0 : Math.round((canvas / total) * 100);
  return { total, canvas, d2l, percentMigrated };
};

/**
 * Normalize a facility row from the location-delivery-lms API.
 * @param {Object} row
 * @returns {Object|null}
 */
export const toFacilityRow = (row) => {
  if (!row || typeof row !== 'object') return null;

  const locationKey = row.locationKey ?? row.locationId ?? row.id;
  if (locationKey == null || locationKey === '') return null;

  return {
    locationKey: String(locationKey),
    locationName: row.locationName ?? row.name ?? '',
    shortCode: row.shortCode ?? row.locationShortCode ?? '',
    currentDeliveryLms: normalizeDeliveryLms(row.currentDeliveryLms ?? row.deliveryLms),
    defaultApplied: Boolean(row.defaultApplied),
    learnerCount: toLearnerCount(row),
    // Stage 2 provisioning hints — forwarded when the API surfaces them so the
    // provisioning panel can gate "Provision Existing Learners" correctly. When
    // absent the panel falls back to `currentDeliveryLms === 'canvas'`.
    canvasSubAccountId: toCanvasSubAccountId(row),
    parentProvisioned: toParentProvisioned(row),
  };
};

const toCanvasSubAccountId = (row) => {
  const value = row?.canvasSubAccountId ?? row?.canvasSubAccountID ?? null;
  if (value == null || value === '') return null;
  return String(value);
};

const toParentProvisioned = (row) => {
  if (row?.parentProvisioned == null) return null;
  return Boolean(row.parentProvisioned);
};

/**
 * Extract an active-learner count from a facility payload when the API
 * provides one. Returns null when no usable count is present so the UI can
 * fall back to a generic, non-fabricated impact message.
 * @param {Object} row
 * @returns {number|null}
 */
export const toLearnerCount = (row) => {
  const raw =
    row?.learnerCount ?? row?.activeLearners ?? row?.activeLearnerCount ?? row?.enrolledLearners;
  const count = Number(raw);
  return Number.isFinite(count) && count >= 0 ? count : null;
};

/**
 * Sum the known learner counts across the supplied facilities. Returns null
 * when none of the facilities expose a count, signalling that no real figure
 * is available to display.
 * @param {Array} rows
 * @returns {number|null}
 */
export const sumLearnerCounts = (rows = []) => {
  const counted = rows.filter((row) => Number.isFinite(row?.learnerCount));
  if (counted.length === 0) return null;
  return counted.reduce((total, row) => total + row.learnerCount, 0);
};

/**
 * Normalize list API responses that may be a bare array or wrapped payload.
 * @param {*} response
 * @returns {Array}
 */
export const toFacilityList = (response) => {
  if (Array.isArray(response)) {
    return response.map(toFacilityRow).filter(Boolean);
  }

  const candidates = [
    response?.data,
    response?.facilities,
    response?.items,
    response?.results,
    response?.content,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.map(toFacilityRow).filter(Boolean);
    }
  }

  return [];
};

/**
 * Normalize history API responses.
 * @param {*} response
 * @returns {Array}
 */
export const toFacilityHistoryList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.history)) return response.history;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};
